import { useRootNavigationState } from 'expo-router'
import { useMemo } from 'react'

interface IPreviousRouteInfo {
  name?: string
  params?: Record<string, string | number | boolean>
  key?: string
}

interface INavigationState {
  index: number
  routes: IPreviousRouteInfo[]
}

interface IUsePreviousRoute {
  previousRoute: IPreviousRouteInfo | null
  backLabel: string
  navigationState: INavigationState | null
}

/**
 * Hook customizado para detectar a rota anterior e determinar o label apropriado
 * para o botão de voltar baseado no contexto de navegação.
 *
 * @returns {Object} Objeto contendo:
 * - previousRoute: Informações sobre a rota anterior (nome, parâmetros, chave)
 * - backLabel: Label apropriado para o botão de voltar baseado na rota anterior
 * - navigationState: Estado completo de navegação do React Navigation
 *
 * @example
 * ```tsx
 * import { usePreviousRoute } from '@hooks/usePreviousRoute'
 *
 * export default function MyScreen() {
 *   const { previousRoute, backLabel } = usePreviousRoute()
 *
 *   return (
 *     <AppLayout
 *       backRoute={{
 *         label: backLabel, // "Matrículas", "Trilha", "Cursos", etc.
 *         back: () => router.back(),
 *       }}
 *     >
 *       {children}
 *     </AppLayout>
 *   )
 * }
 * ```
 */
export function usePreviousRoute(): IUsePreviousRoute {
  const navigationState = useRootNavigationState()

  // Função para obter a rota anterior do estado de navegação
  const getPreviousRoute = (): IPreviousRouteInfo | null => {
    if (!navigationState?.routes) return null

    const currentRouteIndex = navigationState.index
    if (currentRouteIndex > 0) {
      return navigationState.routes[currentRouteIndex - 1]
    }

    return null
  }

  // Determina o label baseado na rota anterior
  const getBackLabel = useMemo(() => {
    const previousRoute = getPreviousRoute()

    if (!previousRoute) {
      return 'Voltar'
    }

    const routeName = previousRoute.name || ''

    if (routeName === 'index') {
      return 'Início'
    }

    if (routeName.includes('paths')) {
      return 'Meus Produtos'
    }

    return 'Voltar'

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationState])

  return {
    previousRoute: getPreviousRoute(),
    backLabel: getBackLabel,
    navigationState,
  }
}
